import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FOLLOWUP_CHANNEL_LABELS,
  FOLLOWUP_MAX_REPEATS,
  FOLLOWUP_UNIT_LABELS,
  TEMPLATE_VARIABLES,
  followupOccurrences,
  formatFollowupDelay,
  type FollowupChannel,
  type FollowupUnit,
  type ServiceFollowup,
  type TemplatePreview,
} from "@shared/modules/x-messagerie";
import {
  groupServiceVariants,
  type CategoryWithServices,
  type ServiceVariantGroup,
} from "@shared/modules/x-atelier";
import { api } from "../../../core/lib/api";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { PageHeader } from "../../../core/components/ui/page-header";
import { useToastStore } from "../../../core/stores/toast";
import { ChannelBadge } from "../components/badges";
import { PreviewBlock } from "../components/bubble";
import { HighlightedTextarea, VariableChips, insertVariable } from "../components/variables";
import {
  atelierEnabled,
  createFollowup,
  deleteFollowup,
  listFollowups,
  previewFollowup,
  updateFollowup,
  type FollowupInput,
} from "../lib/api";

// Rappels de suivi par service : « inspection de l'antirouille aux deux ans,
// quatre fois », « scellant à refaire dans 19 semaines ». Chaque rappel est
// rattaché à un service du catalogue (toutes ses déclinaisons comprises) et
// part après l'achèvement d'un travail qui le contient. Les envois planifiés
// se retrouvent dans Messages, en attente, à leur date.

const FORM_ID = "messagerie-followup-form";

const UNITS: FollowupUnit[] = ["day", "week", "month", "year"];
const CHANNELS: FollowupChannel[] = ["sms", "email", "both"];

/** Variables qui ont un sens deux ans après le travail. */
const VARIABLES = TEMPLATE_VARIABLES.filter((v) =>
  [
    "clientPrenom",
    "clientNom",
    "vehicule",
    "service",
    "adresseAtelier",
    "telephoneAtelier",
    "nomAtelier",
  ].includes(v.name),
);

const DEFAULT_BODY =
  "Bonjour {{clientPrenom}}, petit rappel de {{nomAtelier}} : il est temps de planifier " +
  "l'inspection de votre {{vehicule}}. Appelez-nous au {{telephoneAtelier}} pour prendre " +
  "rendez-vous. Merci et à bientôt !";

interface ServiceOption {
  /** Identifiant envoyé à l'API : la première déclinaison du service. */
  id: string;
  name: string;
  categoryName: string;
  /** Toutes les déclinaisons, pour retrouver un rappel posé sur l'une d'elles. */
  ids: Set<string>;
}

function optionsFrom(categories: CategoryWithServices[]): ServiceOption[] {
  return categories.flatMap((c) =>
    groupServiceVariants(c.services).map((g: ServiceVariantGroup) => ({
      id: g.variants[0]!.id,
      name: g.name,
      categoryName: c.name,
      ids: new Set(g.variants.map((v) => v.id)),
    })),
  );
}

interface Draft {
  serviceId: string;
  label: string;
  delayValue: string;
  delayUnit: FollowupUnit;
  repeatCount: number;
  channel: FollowupChannel;
  subject: string;
  body: string;
  enabled: boolean;
}

function draftFrom(f?: ServiceFollowup, serviceId = ""): Draft {
  return {
    serviceId: f?.serviceId ?? serviceId,
    label: f?.label ?? "",
    delayValue: f ? String(f.delayValue).replace(".", ",") : "2",
    delayUnit: f?.delayUnit ?? "year",
    repeatCount: f?.repeatCount ?? 0,
    channel: f?.channel ?? "sms",
    subject: f?.subject ?? "",
    body: f?.body ?? DEFAULT_BODY,
    enabled: f?.enabled ?? true,
  };
}

/** « ×4 : à 2, 4, 6 et 8 ans ». */
function occurrencesLabel(d: Pick<Draft, "delayValue" | "delayUnit" | "repeatCount">): string {
  const value = Number(d.delayValue.replace(",", "."));
  if (!Number.isFinite(value) || value <= 0) return "";
  const list = followupOccurrences({
    delayValue: value,
    delayUnit: d.delayUnit,
    repeatCount: d.repeatCount,
  });
  if (list.length === 1)
    return `Un seul envoi, ${formatFollowupDelay(value, d.delayUnit)} après le travail.`;
  const [, plural] = FOLLOWUP_UNIT_LABELS[d.delayUnit];
  const points = list.map((o) =>
    new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 2 }).format(o.value),
  );
  return `${list.length} envois : à ${points.slice(0, -1).join(", ")} et ${points[points.length - 1]} ${plural} après le travail.`;
}

/** Page « Rappels » (admin) : liste par service, création et modification en fenêtre. */
export function RappelsPage() {
  const push = useToastStore((s) => s.push);
  const [searchParams, setSearchParams] = useSearchParams();
  const focus = searchParams.get("service") ?? "";
  const [followups, setFollowups] = useState<ServiceFollowup[] | null>(null);
  const [options, setOptions] = useState<ServiceOption[]>([]);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<{ open: boolean; followup?: ServiceFollowup }>({
    open: false,
  });
  const [confirm, setConfirm] = useState<ServiceFollowup | null>(null);

  const load = useCallback(() => {
    listFollowups()
      .then((list) => {
        setFollowups(list);
        setFailed(false);
      })
      .catch(() => {
        setFollowups([]);
        setFailed(true);
      });
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    if (!atelierEnabled) return;
    api<CategoryWithServices[]>("/api/atelier/catalogue")
      .then((list) => setOptions(optionsFrom(list)))
      .catch(() => setOptions([]));
  }, []);

  const optionOf = useCallback(
    (serviceId: string) => options.find((o) => o.ids.has(serviceId)) ?? null,
    [options],
  );
  const focusOption = focus ? optionOf(focus) : null;

  const shown = useMemo(() => {
    if (!followups) return [];
    if (!focusOption) return followups;
    return followups.filter((f) => focusOption.ids.has(f.serviceId));
  }, [followups, focusOption]);

  // Regroupés par service, dans l'ordre du catalogue ; les rappels d'un
  // service disparu du catalogue restent visibles, à la fin.
  const grouped = useMemo(() => {
    const byOption = new Map<string, ServiceFollowup[]>();
    const orphans: ServiceFollowup[] = [];
    for (const f of shown) {
      const option = optionOf(f.serviceId);
      if (!option) {
        orphans.push(f);
        continue;
      }
      byOption.set(option.id, [...(byOption.get(option.id) ?? []), f]);
    }
    const sections = options
      .filter((o) => byOption.has(o.id))
      .map((o) => ({
        key: o.id,
        title: o.name,
        subtitle: o.categoryName,
        items: byOption.get(o.id)!,
      }));
    if (orphans.length)
      sections.push({
        key: "orphans",
        title: "Service retiré du catalogue",
        subtitle: "",
        items: orphans,
      });
    return sections;
  }, [shown, options, optionOf]);

  const run = async (
    work: () => Promise<unknown>,
    success: string,
    failure = "Opération impossible",
  ) => {
    if (busy) return false;
    setBusy(true);
    try {
      await work();
      push("success", success);
      load();
      return true;
    } catch {
      push("danger", failure);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const toggle = (f: ServiceFollowup) =>
    void run(
      () => updateFollowup(f.id, { enabled: !f.enabled }),
      f.enabled ? "Rappel désactivé" : "Rappel activé",
    );

  const remove = async () => {
    if (!confirm) return;
    const ok = await run(
      () => deleteFollowup(confirm.id),
      "Rappel supprimé",
      "Suppression impossible",
    );
    if (ok) setConfirm(null);
  };

  const save = async (input: FollowupInput, id?: string) => {
    const ok = await run(
      () => (id ? updateFollowup(id, input) : createFollowup(input)),
      id ? "Rappel mis à jour" : "Rappel créé",
      "Enregistrement impossible",
    );
    if (ok) setModal({ open: false });
    return ok;
  };

  return (
    <div className="page rappels">
      <PageHeader
        title="Rappels de suivi"
        subtitle="Après un travail terminé, le client reçoit un rappel au délai voulu — par exemple l'inspection de l'antirouille aux deux ans, répétée pendant la garantie."
        actions={
          <Button
            variant="primary"
            icon={<Icon name="plus" size={16} />}
            onClick={() => setModal({ open: true })}
          >
            Nouveau rappel
          </Button>
        }
      />

      {options.length > 0 ? (
        <div className="messagerie-toolbar">
          <label className="messagerie-toolbar__label" htmlFor="rappels-service">
            Service
          </label>
          <Select
            id="rappels-service"
            className="messagerie-toolbar__select"
            value={focusOption?.id ?? ""}
            onChange={(e) => {
              const next = new URLSearchParams(searchParams);
              if (e.target.value) next.set("service", e.target.value);
              else next.delete("service");
              setSearchParams(next, { replace: true });
            }}
          >
            <option value="">Tous les services</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.categoryName} — {o.name}
              </option>
            ))}
          </Select>
          <div className="messagerie-toolbar__spacer" />
          <span className="messagerie-toolbar__count">
            {followups ? `${shown.length} rappel${shown.length > 1 ? "s" : ""}` : "…"}
          </span>
        </div>
      ) : null}

      {followups === null ? (
        <p className="messages__muted">Chargement…</p>
      ) : failed ? (
        <p className="empty">Rappels indisponibles — réessayez dans un instant.</p>
      ) : shown.length === 0 ? (
        <p className="empty">
          {focusOption
            ? `Aucun rappel après « ${focusOption.name} ».`
            : "Aucun rappel de suivi. Créez-en un : par exemple, une inspection aux deux ans après un antirouille permanent, répétée quatre fois."}
        </p>
      ) : (
        <div className="rappels__sections">
          {grouped.map((section) => (
            <section key={section.key} className="card rappels__section">
              <header className="rappels__section-head">
                <h2 className="rappels__section-title">{section.title}</h2>
                {section.subtitle ? (
                  <span className="rappels__section-sub">{section.subtitle}</span>
                ) : null}
              </header>
              <table className="table rappels__table">
                <thead>
                  <tr>
                    <th>Rappel</th>
                    <th>Délai</th>
                    <th>Envois</th>
                    <th>Canal</th>
                    <th>État</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {section.items.map((f) => (
                    <tr key={f.id} className={f.enabled ? "" : "rappels__row--off"}>
                      <td>
                        <span className="rappels__label">{f.label}</span>
                        <span className="rappels__body">{f.body}</span>
                      </td>
                      <td className="rappels__delay">
                        {formatFollowupDelay(f.delayValue, f.delayUnit)}
                      </td>
                      <td className="rappels__delay">
                        {f.repeatCount > 0 ? `×${f.repeatCount + 1}` : "1"}
                      </td>
                      <td>
                        {f.channel === "both" ? (
                          <>
                            <ChannelBadge channel="sms" /> <ChannelBadge channel="email" />
                          </>
                        ) : (
                          <ChannelBadge channel={f.channel} />
                        )}
                      </td>
                      <td>
                        {f.enabled ? (
                          <Badge variant="success">Activé</Badge>
                        ) : (
                          <Badge variant="neutral">Désactivé</Badge>
                        )}
                        {f.sourceKey ? (
                          <span className="rappels__source">repris d'Urable</span>
                        ) : null}
                      </td>
                      <td className="table__actions">
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => toggle(f)}>
                          {f.enabled ? "Désactiver" : "Activer"}
                        </Button>{" "}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setModal({ open: true, followup: f })}
                        >
                          Modifier
                        </Button>{" "}
                        <Button variant="ghost-danger" size="sm" onClick={() => setConfirm(f)}>
                          Supprimer
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}

      {modal.open ? (
        <FollowupModal
          key={modal.followup?.id ?? "new"}
          followup={modal.followup}
          options={options}
          initialServiceId={focusOption?.id ?? ""}
          busy={busy}
          onClose={() => setModal({ open: false })}
          onSave={save}
        />
      ) : null}

      <Modal
        open={confirm !== null}
        title="Supprimer ce rappel ?"
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Annuler
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              Supprimer
            </Button>
          </>
        }
      >
        <p>
          « {confirm?.label} » ne sera plus planifié pour les prochains travaux. Les envois déjà en
          attente dans Messages restent programmés ; annulez-les là au besoin.
        </p>
      </Modal>
    </div>
  );
}

function FollowupModal({
  followup,
  options,
  initialServiceId,
  busy,
  onClose,
  onSave,
}: {
  followup?: ServiceFollowup;
  options: ServiceOption[];
  initialServiceId: string;
  busy: boolean;
  onClose: () => void;
  onSave: (input: FollowupInput, id?: string) => Promise<boolean>;
}) {
  const push = useToastStore((s) => s.push);
  // Un rappel posé sur une déclinaison se présente sur son service.
  const currentOption = followup ? options.find((o) => o.ids.has(followup.serviceId)) : null;
  const [draft, setDraft] = useState<Draft>(() =>
    draftFrom(followup, currentOption?.id ?? initialServiceId),
  );
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // Une variable cliquée s'insère dans le dernier champ touché (message par défaut).
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastField = useRef<"subject" | "body">("body");
  const insert = (name: string) => {
    const field = lastField.current;
    const el = field === "subject" ? subjectRef.current : bodyRef.current;
    set(field, insertVariable(el, draft[field], name));
  };

  const serviceMissing = options.length > 0 && !draft.serviceId;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const delayValue = Number(draft.delayValue.replace(",", "."));
    if (!Number.isFinite(delayValue) || delayValue <= 0) {
      push("danger", "Délai illisible", "Un nombre positif, par exemple 2 ou 1,5.");
      return;
    }
    if (!draft.serviceId) {
      push("danger", "Service manquant", "Choisissez le service qui déclenche le rappel.");
      return;
    }
    if (!draft.label.trim() || !draft.body.trim()) {
      push("danger", "Champs manquants", "Le nom du rappel et le message sont obligatoires.");
      return;
    }
    await onSave(
      {
        serviceId: draft.serviceId,
        label: draft.label.trim(),
        delayValue,
        delayUnit: draft.delayUnit,
        repeatCount: draft.repeatCount,
        channel: draft.channel,
        subject: draft.subject.trim() || null,
        body: draft.body.trim(),
        enabled: draft.enabled,
      },
      followup?.id,
    );
  };

  const showPreview = async () => {
    setPreviewing(true);
    try {
      setPreview(
        await previewFollowup({
          channel: draft.channel,
          subject: draft.subject.trim() || null,
          body: draft.body,
        }),
      );
    } catch {
      push("danger", "Aperçu impossible");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <Modal
      open
      title={followup ? "Modifier le rappel" : "Nouveau rappel de suivi"}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annuler
          </Button>
          <Button variant="ghost" onClick={() => void showPreview()} disabled={previewing || busy}>
            {previewing ? "Aperçu…" : "Aperçu"}
          </Button>
          <Button type="submit" form={FORM_ID} variant="primary" disabled={busy || serviceMissing}>
            {followup ? "Enregistrer" : "Créer le rappel"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="rappel-form" onSubmit={(e) => void submit(e)}>
        <div className="form-grid">
          <div className="form-grid__full">
            <Field
              label="Après le service"
              hint="Toutes les déclinaisons du service comptent (coupé, VUS, camionnette…)."
            >
              {options.length > 0 ? (
                <Select
                  value={draft.serviceId}
                  onChange={(e) => set("serviceId", e.target.value)}
                  required
                >
                  <option value="">Choisir un service…</option>
                  {options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.categoryName} — {o.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  value={draft.serviceId}
                  onChange={(e) => set("serviceId", e.target.value)}
                  placeholder="Identifiant du service"
                />
              )}
            </Field>
          </div>
          <div className="form-grid__full">
            <Field label="Nom du rappel" hint="Pour vous repérer dans la liste.">
              <Input
                value={draft.label}
                onChange={(e) => set("label", e.target.value)}
                placeholder="ex : Inspection antirouille"
                autoFocus
                required
              />
            </Field>
          </div>
          <Field label="Délai après le travail">
            <div className="rappel-form__delay">
              <Input
                inputMode="decimal"
                value={draft.delayValue}
                onChange={(e) => set("delayValue", e.target.value)}
                aria-label="Valeur du délai"
                required
              />
              <Select
                value={draft.delayUnit}
                onChange={(e) => set("delayUnit", e.target.value as FollowupUnit)}
                aria-label="Unité du délai"
              >
                {UNITS.map((u) => (
                  <option key={u} value={u}>
                    {FOLLOWUP_UNIT_LABELS[u][1]}
                  </option>
                ))}
              </Select>
            </div>
          </Field>
          <Field
            label="Répétitions"
            hint="Envois supplémentaires au même intervalle : 3 répétitions aux 2 ans couvrent une garantie de 10 ans."
          >
            <Select
              value={draft.repeatCount}
              onChange={(e) => set("repeatCount", Number(e.target.value))}
            >
              {Array.from({ length: FOLLOWUP_MAX_REPEATS + 1 }, (_, n) => (
                <option key={n} value={n}>
                  {n === 0 ? "Aucune (un seul envoi)" : `${n} — ${n + 1} envois au total`}
                </option>
              ))}
            </Select>
          </Field>
          <div className="form-grid__full">
            <p className="rappel-form__occurrences">{occurrencesLabel(draft)}</p>
          </div>
          <Field
            label="Canal"
            hint="Sans l'adresse voulue, le client reçoit le message par l'autre canal."
          >
            <Select
              value={draft.channel}
              onChange={(e) => set("channel", e.target.value as FollowupChannel)}
            >
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {FOLLOWUP_CHANNEL_LABELS[c]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Objet" hint="Courriel seulement ; vide = le nom du rappel.">
            <Input
              ref={subjectRef}
              value={draft.subject}
              onChange={(e) => set("subject", e.target.value)}
              onFocus={() => (lastField.current = "subject")}
            />
          </Field>
          <div className="form-grid__full">
            <Field label="Message" hint="Les variables surlignées sont remplacées à l’envoi.">
              <HighlightedTextarea
                ref={bodyRef}
                rows={5}
                value={draft.body}
                onChange={(e) => set("body", e.target.value)}
                onFocus={() => (lastField.current = "body")}
                required
              />
            </Field>
          </div>
        </div>

        <VariableChips variables={VARIABLES} onInsert={insert} open />

        <label className="modele__toggle">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => set("enabled", e.target.checked)}
          />
          {draft.enabled ? "Activé" : "Désactivé"}
        </label>

        {preview ? <PreviewBlock preview={preview} fallbackTo="Exemple" /> : null}
      </form>
    </Modal>
  );
}
